import React, { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { useRegisterFinishMutation } from '../generated/urql.anonymous'
import { useI18n } from '../provider/i18n'
import PatientLogo from '../assets/patient-logo.svg'
import { WelcomeFrame } from '../components/WelcomeFrame'
import IconInput from '../components/IconInput'
import { LuTextCursorInput } from 'react-icons/lu'
import { MdEmail } from 'react-icons/md'
import { Blue300 } from '../lib/colors'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { MessageBox } from '../components/MessageBox'
import { EmailRegex } from '../lib/patterns'

const EmailIcon = MdEmail as any

export const RegisterFinishPage = () => {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const i18n = useI18n()
  const navigate = useNavigate()
  const [{ fetching: registerFinishFetching }, registerFinish] =
    useRegisterFinishMutation()
  const [generalError, setGeneralError] = useState<string>()
  const [showRetry, setShowRetry] = useState(false)

  const emailFromParams =
    searchParams.get('email') || location.state?.email || ''
  const hasEmailFromParams = Boolean(emailFromParams)

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      code: searchParams.get('code') || '',
      email: emailFromParams,
    },
  })

  useEffect(() => {
    setValue('code', searchParams.get('code') || '')
  }, [setValue, searchParams])

  const doRegisterFinish = useCallback(
    async ({ code, email }: { code: string; email: string }) => {
      setGeneralError(undefined)
      setShowRetry(false)
      const { data, error } = await registerFinish({
        email,
        code,
      })

      if (error) {
        switch (error.graphQLErrors[0]?.extensions.code) {
          case 'missing-token':
            setGeneralError(
              i18n.t('screens.registerFinish.generalError.missingToken')
            )
            break
          case 'invalid-code':
            setGeneralError(
              i18n.t('screens.registerFinish.generalError.invalidCode')
            )
            break
          case 'client-not-found':
            setGeneralError(
              i18n.t('screens.registerFinish.generalError.clientNotFound')
            )
            break
          default:
            setGeneralError(
              i18n.t('screens.registerFinish.generalError.unknown')
            )
            setShowRetry(true)
        }
      }

      if (data?.client_register_finish) {
        navigate(`/login`, { state: { email } })
      }
    },
    [registerFinish, i18n, navigate]
  )

  useEffect(() => {
    const code = searchParams.get('code')
    const email = searchParams.get('email') || location.state?.email
    if (code && email) {
      doRegisterFinish({ code, email }).catch(console.error)
    }
  }, [searchParams, doRegisterFinish, location.state?.email])

  if (searchParams.get('code')) {
    return (
      <WelcomeFrame className="flex flex-col items-center justify-around pt-4 px-5">
        <div className="my-5">
          <img src={PatientLogo} alt="HomeLab Patient app logo" />
        </div>
        <p className="text-center">
          {i18n.t('screens.registerFinish.message.confirmingRegistration')}
        </p>
        {generalError && <MessageBox>{generalError}</MessageBox>}
        {showRetry && (
          <div className="my-4">
            <button
              className="hl-button-lg"
              onClick={() =>
                doRegisterFinish({
                  code: searchParams.get('code') || '',
                  email: searchParams.get('email') || location.state?.email || '',
                }).catch(console.error)
              }
              disabled={registerFinishFetching}
            >
              {i18n.t('screens.registerFinish.retry.button')}
            </button>
          </div>
        )}
      </WelcomeFrame>
    )
  }

  return (
    <WelcomeFrame className="flex flex-col items-center justify-around pt-4">
      <div className="flex-1 flex flex-col justify-center items-center">
        <div className="my-5">
          <img src={PatientLogo} alt="HomeLab Patient app logo" />
        </div>
        <p>{i18n.t('screens.registerFinish.message.codeSentToEmail')}</p>
      </div>
      <div className="flex-1 my-0 mx-auto w-[320px] bg-blue-100 bg-opacity-50">
        {generalError && <MessageBox>{generalError}</MessageBox>}
        <form onSubmit={handleSubmit(doRegisterFinish)}>
          <div className="mx-4">
            {!hasEmailFromParams && (
              <div className="my-4">
                <IconInput
                  leftIcon={<EmailIcon size={24} color={Blue300} />}
                  {...register('email', {
                    required: i18n.t(
                      'screens.register.form.email.rules.required'
                    ),
                    pattern: {
                      value: EmailRegex,
                      message: i18n.t(
                        'screens.register.form.email.rules.pattern'
                      ),
                    },
                  })}
                  placeholder={i18n.t(
                    'screens.register.form.email.placeholder'
                  )}
                />
                {errors.email?.message && (
                  <p className="hl-input-error">{String(errors.email.message)}</p>
                )}
              </div>
            )}
            <div className="my-4">
              <IconInput
                leftIcon={<LuTextCursorInput size={24} color={Blue300} />}
                {...register('code', {
                  required: i18n.t(
                    'screens.registerFinish.form.code.rules.required'
                  ),
                })}
                placeholder={i18n.t(
                  'screens.registerFinish.form.code.placeholder'
                )}
              />
              {errors.code && (
                <p className="hl-input-error">{errors.code.message}</p>
              )}
            </div>
          </div>
          <div className="my-4">
            <button
              className="hl-button-lg"
              type="submit"
              disabled={registerFinishFetching}
            >
              {i18n.t('screens.registerFinish.form.submit.button')}
            </button>
          </div>
        </form>
        <p className="m-4 text-center">
          <Link to={`/login`}>
            {i18n.t('screens.registerFinish.links.login.text')}
          </Link>
        </p>
      </div>
    </WelcomeFrame>
  )
}
